// ====================================================================================
// PÁGINA DE SUPORTE - CLIENTE
// ====================================================================================
// PROMPT BLINDADO: Visualização de tickets de suporte do cliente
// ====================================================================================

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  ArrowLeft, RefreshCw, Plus, MessageSquare, Clock, CheckCircle,
  AlertCircle, Send, Loader2, User, Shield
} from 'lucide-react';
import SupportModal from '@/components/SupportModal';

const STATUS_CONFIG = {
  pending: { label: 'Aguardando', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  open: { label: 'Em Aberto', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  in_progress: { label: 'Em Análise', color: 'bg-purple-100 text-purple-700', icon: MessageSquare },
  resolved: { label: 'Resolvido', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  closed: { label: 'Fechado', color: 'bg-slate-100 text-slate-700', icon: CheckCircle }
};

const CATEGORY_LABELS = {
  service: 'Serviço / Agendamento',
  payment: 'Pagamento / Cobrança',
  account: 'Minha Conta',
  technical: 'Problema Técnico',
  other: 'Outro'
};

export default function ClientSupport() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/client/login');
      return;
    }
    fetchTickets();
  }, [user, navigate]);

  const fetchTickets = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(res.data.tickets || []);
    } catch (error) {
      console.error('Erro ao carregar tickets:', error);
      toast.error('Erro ao carregar tickets');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const openTicketDetail = async (ticketId) => {
    setDetailLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/support/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTicket(res.data.ticket);
    } catch (error) {
      toast.error('Erro ao carregar detalhes do ticket');
    } finally {
      setDetailLoading(false);
    }
  };

  const sendReply = async () => {
    if (!newMessage.trim() || !selectedTicket) return;
    
    setSendingMessage(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/support/tickets/${selectedTicket.id}/reply`, 
        { message: newMessage.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Mensagem enviada!');
      setNewMessage('');
      // Recarregar detalhes do ticket
      openTicketDetail(selectedTicket.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar mensagem');
    } finally {
      setSendingMessage(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-4">
        <div className="max-w-4xl mx-auto space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/client/dashboard')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-lg font-semibold">Meus Tickets de Suporte</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => fetchTickets(true)}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" onClick={() => setShowNewTicketModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Novo Ticket
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-4">
        {tickets.length === 0 ? (
          <Card className="p-8 text-center">
            <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium mb-2">Nenhum ticket de suporte</h3>
            <p className="text-slate-500 mb-4">
              Você ainda não abriu nenhum ticket de suporte.
            </p>
            <Button onClick={() => setShowNewTicketModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Abrir Ticket
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {tickets.map(ticket => {
              const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.pending;
              const StatusIcon = statusConfig.icon;
              
              return (
                <Card 
                  key={ticket.id}
                  className="p-4 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => openTicketDetail(ticket.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={statusConfig.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                        <span className="text-xs text-slate-400">
                          #{ticket.id.slice(0, 8)}
                        </span>
                      </div>
                      <h3 className="font-medium">{ticket.subject}</h3>
                      <p className="text-sm text-slate-500 mt-1">
                        {CATEGORY_LABELS[ticket.category] || ticket.category}
                      </p>
                    </div>
                    <div className="text-right text-xs text-slate-400">
                      <p>{formatDate(ticket.created_at)}</p>
                      {ticket.messages?.length > 1 && (
                        <p className="mt-1">
                          {ticket.messages.length} mensagens
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Ticket Detail Modal */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Ticket #{selectedTicket?.id?.slice(0, 8)}
            </DialogTitle>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : selectedTicket && (
            <>
              {/* Ticket Info */}
              <div className="border-b pb-3 mb-3">
                <h3 className="font-medium">{selectedTicket.subject}</h3>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className={STATUS_CONFIG[selectedTicket.status]?.color || 'bg-slate-100'}>
                    {STATUS_CONFIG[selectedTicket.status]?.label || selectedTicket.status}
                  </Badge>
                  <span className="text-xs text-slate-400">
                    {CATEGORY_LABELS[selectedTicket.category] || selectedTicket.category}
                  </span>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto space-y-3 max-h-[300px]">
                {(selectedTicket.messages || []).map((msg, idx) => {
                  const isAdmin = msg.sender_type === 'admin';
                  return (
                    <div 
                      key={msg.id || idx}
                      className={`flex ${isAdmin ? 'justify-start' : 'justify-end'}`}
                    >
                      <div className={`max-w-[80%] p-3 rounded-lg ${
                        isAdmin 
                          ? 'bg-blue-50 border border-blue-100' 
                          : 'bg-slate-100'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          {isAdmin ? (
                            <Shield className="w-3 h-3 text-blue-600" />
                          ) : (
                            <User className="w-3 h-3 text-slate-500" />
                          )}
                          <span className="text-xs font-medium">
                            {isAdmin ? 'Suporte' : 'Você'}
                          </span>
                          <span className="text-xs text-slate-400">
                            {formatDate(msg.created_at)}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Reply Input */}
              {selectedTicket.status !== 'closed' && (
                <div className="border-t pt-3 mt-3">
                  <Textarea
                    placeholder="Digite sua mensagem..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    rows={2}
                    disabled={sendingMessage}
                  />
                  <div className="flex justify-end mt-2">
                    <Button 
                      onClick={sendReply}
                      disabled={!newMessage.trim() || sendingMessage}
                    >
                      {sendingMessage ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Enviar
                    </Button>
                  </div>
                </div>
              )}

              {selectedTicket.status === 'closed' && (
                <div className="text-center py-3 bg-slate-50 rounded-lg text-sm text-slate-500">
                  Este ticket foi encerrado
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* New Ticket Modal */}
      <SupportModal 
        isOpen={showNewTicketModal}
        onClose={() => setShowNewTicketModal(false)}
        onTicketCreated={() => {
          setShowNewTicketModal(false);
          fetchTickets(true);
        }}
      />
    </div>
  );
}
